/**
 * FartStormModal — celebratory popup that fires the first time a
 * fart preset loads in a session. Renders a backdrop + centered card
 * with a big swirly graphic and the catchphrase. Dismissable via
 * the close button, the backdrop, or the Escape key.
 *
 * Built imperatively (not via PresetBrowser's modal pattern) because
 * this is fire-and-forget chrome, not a CRUD surface — every show
 * builds a fresh DOM, every dismiss tears it down.
 */

const CATCHPHRASE = "Hold onto your butts, there's a fart storm brewin'";

export interface FartStormModalOptions {
  /**
   * Called once when the modal is dismissed (button click, backdrop
   * click, or Escape). Used by main.ts to load Cheek Flapper +
   * scroll the preset browser to it after the user closes the
   * popup. Errors inside the callback are swallowed so a buggy hook
   * can't keep the modal stuck on screen.
   */
  readonly onDismiss?: () => void;
}

/**
 * Show the fart-storm modal. No-op if a modal is already on screen
 * (rapidly loading two fart presets in a row only fires one popup).
 * Returns a tear-down function callers usually don't need; the
 * modal removes itself on dismiss.
 */
export function showFartStormModal(opts: FartStormModalOptions = {}): () => void {
  if (document.querySelector('.fartstorm-modal-backdrop')) {
    return () => undefined;
  }

  const backdrop = document.createElement('div');
  backdrop.className = 'fartstorm-modal-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'Fart storm warning');

  const card = document.createElement('div');
  card.className = 'fartstorm-modal';

  // Big swirly art — pure SVG so it scales / themes without an image
  // asset. Three offset wind whorls in the accent colors with a
  // squiggly cheek-flapper lip below for the visual punchline.
  const art = document.createElement('div');
  art.className = 'fartstorm-modal-art';
  art.innerHTML = `
    <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <radialGradient id="fartstorm-cloud" cx="0.5" cy="0.5" r="0.6">
          <stop offset="0%" stop-color="#7a4f33" stop-opacity="0.85"/>
          <stop offset="60%" stop-color="#3a2515" stop-opacity="0.6"/>
          <stop offset="100%" stop-color="#1a0f08" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <ellipse cx="100" cy="80" rx="90" ry="55" fill="url(#fartstorm-cloud)"/>
      <g stroke="#e08020" stroke-width="3" fill="none" stroke-linecap="round">
        <path d="M50,55 Q70,45 80,55 T110,55 T140,55"/>
        <path d="M40,80 Q65,68 80,80 T120,80 T160,80"/>
        <path d="M55,105 Q75,95 85,105 T120,105 T145,105"/>
      </g>
      <g fill="#e0a020">
        <circle cx="62" cy="35" r="3"/>
        <circle cx="148" cy="42" r="2.5"/>
        <circle cx="155" cy="115" r="3"/>
        <circle cx="45" cy="125" r="2"/>
      </g>
    </svg>
    <div class="fartstorm-modal-emoji" aria-hidden="true">💨</div>
  `;

  const text = document.createElement('p');
  text.className = 'fartstorm-modal-text';
  text.textContent = CATCHPHRASE;

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn fartstorm-modal-close';
  closeBtn.textContent = "Let 'er rip";

  let removed = false;
  const teardown = (): void => {
    if (removed) return;
    removed = true;
    document.removeEventListener('keydown', onKey);
    backdrop.remove();
    if (opts.onDismiss) {
      try {
        opts.onDismiss();
      } catch {
        // Hook errors shouldn't keep the modal half-dismissed.
      }
    }
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      teardown();
    }
  };
  document.addEventListener('keydown', onKey);

  closeBtn.addEventListener('click', teardown);
  backdrop.addEventListener('click', (e) => {
    // Only dismiss when the backdrop itself is clicked — clicks
    // inside the card shouldn't bubble through and close the modal.
    if (e.target === backdrop) teardown();
  });

  card.append(art, text, closeBtn);
  backdrop.appendChild(card);
  document.body.appendChild(backdrop);
  // Defer focus so the close button is the first focusable target —
  // keyboard users hitting Enter dismiss without hunting for it.
  queueMicrotask(() => closeBtn.focus());

  return teardown;
}
